import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {object} AngebotButtonRefs
 * @property {HTMLElement} [root] - Wrapper carrying the product URL and the selected variant id.
 * @property {HTMLAnchorElement} [link] - The button itself.
 */

/**
 * Keeps the „Bestpreis-Angebot" button's target URL in step with the page.
 *
 * Two things have to be picked up client-side, and neither can be done in Liquid:
 *
 * 1. The variant. `variant-picker.js` morphs *only* the variant picker element on a variant change
 *    ("By default, only the variant picker is morphed", variant-picker.js:305), so this block is
 *    never re-rendered and the server-side `?variant=` would go stale. The id is read straight off
 *    the event, not off `location.search`: the picker pushes the new URL inside
 *    `yieldToMainThread().then(...)` (variant-picker.js:110-113), i.e. after this handler has run.
 *
 * 2. The accessories. Which Candy Bar rows are ticked exists only in the DOM.
 *
 * Everything here is enhancement. The link is rendered complete by Liquid; without JavaScript it
 * still leads to the form with the right product and variant, just without the accessory
 * pre-selection — and the form renders the full accessory list anyway.
 *
 * @extends Component<AngebotButtonRefs>
 */
class AngebotButton extends Component {
  /**
   * The section we listen on, remembered rather than looked up.
   * `closest()` returns null once the element is detached, so resolving it in
   * `disconnectedCallback` would silently skip the cleanup and leak the listeners.
   * @type {Element | null}
   */
  #section = null;

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest('.shopify-section, dialog');

    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    // Capture phase: the Candy Bar's checkboxes carry Horizon's declarative `on:change` binding,
    // and capture runs before anything could stop the event on its way up.
    this.#section?.addEventListener('change', this.#onChange, true);
    this.refs.link?.addEventListener('click', this.#onChange);

    this.#refresh();
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#section?.removeEventListener('change', this.#onChange, true);
    this.refs.link?.removeEventListener('click', this.#onChange);
    this.#section = null;

    super.disconnectedCallback();
  }

  /**
   * @param {Event} event
   */
  #onProductSelect = (event) => {
    const { detail } = /** @type {{ detail?: { variantId?: string } }} */ (/** @type {unknown} */ (event));
    const { root } = this.refs;

    if (!(root instanceof HTMLElement)) return;

    // An unavailable option combination resolves without a variant id. Drop the parameter then, so
    // the form falls back to the first available variant instead of quoting a variant nobody picked.
    if (detail?.variantId) root.dataset.variantId = String(detail.variantId);
    else delete root.dataset.variantId;

    this.#refresh();
  };

  #onChange = () => this.#refresh();

  /** Rewrites the link's href from the current variant and accessory selection. */
  #refresh() {
    const { root, link } = this.refs;

    if (!(root instanceof HTMLElement) || !(link instanceof HTMLAnchorElement)) return;

    const base = root.dataset.baseUrl;
    if (!base) return;

    const url = new URL(base, window.location.origin);
    url.searchParams.set('view', 'angebot');

    const variantId = root.dataset.variantId;
    if (variantId) url.searchParams.set('variant', variantId);
    else url.searchParams.delete('variant');

    const handles = this.#selectedAccessories();
    if (handles.length > 0) url.searchParams.set('zubehoer', handles.join(','));
    else url.searchParams.delete('zubehoer');

    link.href = `${url.pathname}${url.search}`;
  }

  /**
   * The handles of the Candy Bar rows the buyer has ticked, in display order.
   * Scoped to this section so a quick-add drawer cannot bleed into the product page behind it.
   * @returns {string[]}
   */
  #selectedAccessories() {
    const scope = this.#section ?? document;
    const rows = scope.querySelectorAll('candy-bar-component .candy-bar__item');

    /** @type {string[]} */
    const handles = [];

    for (const row of rows) {
      if (!(row instanceof HTMLElement)) continue;

      const toggle = row.querySelector('.candy-bar__checkbox');
      if (!(toggle instanceof HTMLInputElement) || !toggle.checked || toggle.disabled) continue;

      const handle = row.dataset.itemHandle;
      if (handle) handles.push(handle);
    }

    return handles;
  }
}

if (!customElements.get('angebot-button-component')) {
  customElements.define('angebot-button-component', AngebotButton);
}
